/** @file Undo, redo and per-row revert over POST /revert, each a step of the own serial send queue. */
import { makeError } from '@pose2d/format/pose2dError';
import { liveStore } from '../../stores/liveStore';
import { liveSession, type SyncSession } from '../sync/liveSession';
import { feedPages } from '../sync/feedPages';
import { kickSync } from '../sync/kickSync';
import { pushNotice } from '../sync/pushNotice';
import { whenConfirmed } from '../sync/whenConfirmed';
import { redoTarget } from './redoTarget';

/** The rev to revert, undefined for the server's default target, null for nothing to redo. */
type TargetOf = (session: SyncSession) => number | undefined | null;

/** Reverts what `target` names, then waits until the editor holds the revert commit, so the next step picks from a
 *  feed that has it; a refusal becomes a notice. Never rejects. The sync loop fetches the commit over REST whatever
 *  the socket state (a half-open socket brings no frame, fx1 T-463), and a commit reached through a resync is read
 *  into the feed before the step ends. */
async function revertStep(session: SyncSession, target: TargetOf): Promise<void> {
  if (liveSession.get() !== session) return;
  const rev = target(session);
  if (rev === null) {
    pushNotice(null, makeError('NOT_FOUND', { kind: 'commit', ref: 'redo' }, { hint: 'nothing to redo: redo follows an undo, before any new edit' }));
    return;
  }
  let committed: number;
  try {
    committed = (await session.client.revert(session.projectId, rev)).rev;
  } catch (thrown) {
    if (liveSession.get() === session) pushNotice(null, thrown);
    return;
  }
  if (liveSession.get() !== session) return;
  session.knownRev = Math.max(session.knownRev, committed);
  void kickSync(session);
  await whenConfirmed(committed);
  if (liveSession.get() === session) await feedPages.holding(session, committed);
}

/** Queues one history step behind every earlier batch and step of the session (E8-R6): the target is picked after
 *  the drain, so a batch still in flight counts, and a later dispatch is sent only once the revert is confirmed. */
function queued(target: TargetOf): Promise<void> {
  const session = liveSession.get();
  if (session === null) return Promise.resolve();
  const run = session.queue.then(() => revertStep(session, target));
  session.queue = run.then(
    () => undefined,
    () => undefined,
  );
  return run;
}

/** The three history actions of the live project (E8-R6); the commits arrive as ordinary frames. */
export const history = {
  /** Mod+KeyZ: the actor's newest commit that is neither a revert nor reverted (the server picks it). */
  undo: (): Promise<void> => queued(() => undefined),
  /** Mod+Shift+KeyZ / Mod+KeyY: reverts the newest live undo, or leaves a NOT_FOUND notice. */
  redo: (): Promise<void> => queued((session) => redoTarget(liveStore.getState().commits, session.actor)),
  /** The Activity row button: reverts that commit (REVERT_CONFLICT becomes a notice). */
  revert: (rev: number): Promise<void> => queued(() => rev),
};
